import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CustomerQboFinance,
  QboAuthorizeResult,
  QboBatchItemResult,
  QboCallbackInput,
  QboConnection,
  QboInvoiceMapping,
  QboPaymentSyncResult,
  QboPaymentSyncSchedule,
  QboRetryEmailInput,
  QboSendInvoiceInput,
  QboSendPreview,
  QboSettings,
  QboSyncLogEntry,
  UpdateQboPaymentSyncScheduleInput,
  UpdateQboSettingsInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const qboKeys = {
  connection: ['qbo', 'connection'] as const,
  settings: ['qbo', 'settings'] as const,
  paymentSync: ['qbo', 'payment-sync'] as const,
  invoiceMapping: (invoiceId: string) => ['qbo', 'invoice-mapping', invoiceId] as const,
  sendPreview: (invoiceId: string) => ['qbo', 'send-preview', invoiceId] as const,
  syncLog: (invoiceId?: string) => ['qbo', 'sync-log', invoiceId ?? 'all'] as const,
  customerFinance: (customerId: string) => ['qbo', 'customer-finance', customerId] as const,
};

export function useQboConnection(): UseQueryResult<QboConnection> {
  return useQuery({
    queryKey: qboKeys.connection,
    queryFn: () => api.get<QboConnection>('/qbo/connection'),
  });
}

export function useQboSettings(): UseQueryResult<QboSettings> {
  return useQuery({
    queryKey: qboKeys.settings,
    queryFn: () => api.get<QboSettings>('/qbo/settings'),
  });
}

/** The customer Finance tab payload (QBO balances + invoices + PSA revenue by year). */
export function useCustomerQboFinance(
  customerId: string | undefined,
  enabled = true,
): UseQueryResult<CustomerQboFinance> {
  return useQuery({
    queryKey: qboKeys.customerFinance(customerId ?? ''),
    queryFn: () => api.get<CustomerQboFinance>(`/qbo/customers/${customerId}/finance`),
    enabled: Boolean(customerId) && enabled,
    staleTime: 60_000,
  });
}

/** Per-line preview of the SKU + class an invoice will send to QBO (with sources). */
export function useQboInvoiceMapping(
  invoiceId: string | undefined,
): UseQueryResult<QboInvoiceMapping> {
  return useQuery({
    queryKey: qboKeys.invoiceMapping(invoiceId ?? ''),
    queryFn: () => api.get<QboInvoiceMapping>(`/qbo/invoices/${invoiceId}/mapping-preview`),
    enabled: Boolean(invoiceId),
  });
}

/**
 * The detailed QBO sync log. Pass an `invoiceId` for that invoice's history (invoice
 * detail page), or omit it for the recent cross-invoice log (qbo-batch page).
 */
export function useQboSyncLog(
  invoiceId?: string,
  enabled = true,
): UseQueryResult<QboSyncLogEntry[]> {
  return useQuery({
    queryKey: qboKeys.syncLog(invoiceId),
    queryFn: () =>
      api.get<QboSyncLogEntry[]>(
        invoiceId ? `/qbo/invoices/${invoiceId}/sync-log` : '/qbo/sync-log',
      ),
    enabled,
    staleTime: 15_000,
  });
}

/** Read-only preflight for one invoice: email recipients and anything blocking a send. */
export function useQboSendPreview(
  invoiceId: string | undefined,
  enabled = true,
): UseQueryResult<QboSendPreview> {
  return useQuery({
    queryKey: qboKeys.sendPreview(invoiceId ?? ''),
    queryFn: () => api.get<QboSendPreview>(`/qbo/invoices/${invoiceId}/send-preview`),
    enabled: enabled && Boolean(invoiceId),
  });
}

export interface SendInvoiceToQboArgs {
  id: string;
  input: QboSendInvoiceInput;
}

/** Push one invoice to QBO and, only if that succeeds, email it to the billing contacts. */
export function useSendInvoiceToQbo(): UseMutationResult<
  QboBatchItemResult,
  unknown,
  SendInvoiceToQboArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendInvoiceToQboArgs) =>
      api.post<QboBatchItemResult>(`/qbo/invoices/${id}/send`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: qboKeys.syncLog(id) });
      void queryClient.invalidateQueries({ queryKey: qboKeys.sendPreview(id) });
    },
  });
}

export interface RetryQboEmailArgs {
  id: string;
  input: QboRetryEmailInput;
}

/** Re-send the customer email for an invoice already pushed to QuickBooks. */
export function useRetryQboInvoiceEmail(): UseMutationResult<
  QboBatchItemResult,
  unknown,
  RetryQboEmailArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: RetryQboEmailArgs) =>
      api.post<QboBatchItemResult>(`/qbo/invoices/${id}/retry-email`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: qboKeys.syncLog(id) });
    },
  });
}

/** Save the admin-editable connection settings (client id/secret, redirect, etc.). */
export function useUpdateQboSettings(): UseMutationResult<
  QboSettings,
  unknown,
  UpdateQboSettingsInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQboSettingsInput) =>
      api.put<QboSettings>('/qbo/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qboKeys.settings });
      void queryClient.invalidateQueries({ queryKey: qboKeys.connection });
    },
  });
}

/** Start the Intuit OAuth flow; returns the consent URL and CSRF state. */
export function useAuthorizeQbo(): UseMutationResult<QboAuthorizeResult, unknown, void> {
  return useMutation({
    mutationFn: () => api.post<QboAuthorizeResult>('/qbo/connection/authorize'),
  });
}

/** Complete the OAuth flow with the code/realm Intuit redirected back with. */
export function useCompleteQboAuthorization(): UseMutationResult<
  QboConnection,
  unknown,
  QboCallbackInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: QboCallbackInput) =>
      api.post<QboConnection>('/qbo/connection/callback', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qboKeys.connection });
    },
  });
}

export function useRefreshQboConnection(): UseMutationResult<QboConnection, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<QboConnection>('/qbo/connection/refresh'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qboKeys.connection });
    },
  });
}

export function useDisconnectQbo(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/qbo/connection'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qboKeys.connection });
    },
  });
}

/** The scheduled payment-sync settings + last-run summary (admin). */
export function useQboPaymentSyncSchedule(): UseQueryResult<QboPaymentSyncSchedule> {
  return useQuery({
    queryKey: qboKeys.paymentSync,
    queryFn: () => api.get<QboPaymentSyncSchedule>('/qbo/payment-sync'),
  });
}

export function useUpdateQboPaymentSyncSchedule(): UseMutationResult<
  QboPaymentSyncSchedule,
  unknown,
  UpdateQboPaymentSyncScheduleInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQboPaymentSyncScheduleInput) =>
      api.put<QboPaymentSyncSchedule>('/qbo/payment-sync', input),
    onSuccess: (schedule) => {
      queryClient.setQueryData(qboKeys.paymentSync, schedule);
    },
  });
}

/** Run the payment sync now; refreshes the schedule summary and invoice lists. */
export function useRunQboPaymentSync(): UseMutationResult<QboPaymentSyncResult, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<QboPaymentSyncResult>('/qbo/payment-sync/run'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qboKeys.paymentSync });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
